import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { meetingsApi, type Body } from '../features/meetings/api.ts';
import { MeetingForm, type MeetingFormValues } from '../features/meetings/MeetingForm.tsx';
import { termsApi } from '../features/terms/api.ts';
import { orgSettingsApi } from '../features/orgSettings/api.ts';
import { useAuthStore } from '../store/auth.ts';
import { Icon } from '../components/Icon.tsx';
import { dv } from '../i18n/dv.ts';
import { meetingStatus } from '../lib/meetingStatus.ts';
import { formatDvDate, formatDvTime } from '../lib/dvDate.ts';
import { BODIES, schedulerRoles, bodyOfRole } from '../lib/body.ts';
import { Pagination } from '../components/Pagination.tsx';

const PAGE_SIZE = 10;

// Shared status pill variant — matches MeetingDetail/Dashboard so a meeting's
// status reads identically across the app.
export function Meetings(): JSX.Element {
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  // Bodies the current user may schedule a meeting for by ROLE: the body
  // president plus shared staff (SG, Bureau). Empty => no create button (yet).
  const roleBodies = user ? BODIES.filter((b) => schedulerRoles(b).includes(user.role)) : [];

  // A VP / member may ALSO schedule for their own body while they hold the
  // chair (riyaasathu) — the president is on leave and succession has reached
  // them. Only ask the server when their role doesn't already grant it.
  const ownBody = user ? bodyOfRole(user.role) : null;
  const needsChairCheck = !!ownBody && !roleBodies.includes(ownBody);
  const { data: succession } = useQuery({
    queryKey: ['succession', ownBody],
    queryFn: () => orgSettingsApi.succession(ownBody!),
    enabled: needsChairCheck,
  });
  const chairBody = needsChairCheck && succession?.actingChairId === user?.id ? ownBody : null;
  const schedulableBodies = chairBody ? [...roleBodies, chairBody] : roleBodies;
  const canSchedule = schedulableBodies.length > 0;

  const [filter, setFilter] = useState<'upcoming' | 'past'>('upcoming');

  // Body filter for the list. Default to the user's own body if elected,
  // otherwise show all bodies (undefined) for shared staff / viewers.
  const [bodyFilter, setBodyFilter] = useState<Body | null>(ownBody);

  // Term selector: default to the active term (sent as undefined => backend
  // picks the active term); user can pick a specific term or 'all'.
  const { data: terms } = useQuery({ queryKey: ['terms'], queryFn: () => termsApi.list() });
  const [termSel, setTermSel] = useState<string | null>(null); // null = active default
  const term = termSel ?? undefined;

  // Title search, debounced so we don't query per keystroke.
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  // Page resets whenever a filter/search/term/body changes (the result set changes).
  const [page, setPage] = useState(1);
  useEffect(() => { setPage(1); }, [filter, term, debouncedSearch, bodyFilter]);

  const { data, isLoading } = useQuery({
    queryKey: ['meetings', filter, term ?? 'active', debouncedSearch, bodyFilter ?? 'all', page],
    queryFn: () => meetingsApi.list(filter, page, term, debouncedSearch, bodyFilter ?? undefined, undefined, PAGE_SIZE),
  });

  const [showForm, setShowForm] = useState(false);

  const create = useMutation({
    mutationFn: (values: MeetingFormValues) =>
      meetingsApi.create({
        title: values.title,
        scheduledAt: values.scheduledAt,
        location: values.location,
        meetingType: values.meetingType,
        body: values.body,
        recipientIds: values.recipientIds,
      }),
    onSuccess: () => {
      setShowForm(false);
      void qc.invalidateQueries({ queryKey: ['meetings'] });
    },
  });

  return (
    <div className="page space-y-6 lg:space-y-8">
      <header className="flex items-center justify-between">
        <h1 className="text-3xl font-bold tracking-tight">{dv.meetings.title}</h1>
        {canSchedule && (
          <button
            onClick={() => { setShowForm((v) => !v); }}
            className="btn-primary"
          >
            {dv.meetings.create}
          </button>
        )}
      </header>

      {showForm && (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] p-5 lg:p-6 space-y-3">
          <h2 className="status-label text-muted-foreground">{dv.meetings.create}</h2>
          <MeetingForm
            mode="create"
            initial={{ title: '', scheduledAt: '', location: '', meetingType: 'GENERAL', body: schedulableBodies[0] ?? 'COUNCIL', recipientIds: [] }}
            schedulableBodies={schedulableBodies}
            pending={create.isPending}
            errorMessage={create.isError ? dv.common.error : null}
            onSubmit={(values) => create.mutate(values)}
          />
        </section>
      )}

      {/* Toolbar — filter pills + term selector on one side, search on the other,
          laid out so it breathes on wide screens. */}
      <div className="rounded-3xl bg-[hsl(var(--chrome))] flex flex-wrap items-center justify-between gap-x-6 gap-y-3 p-3 sm:px-4">
        <div className="flex flex-wrap items-center gap-2">
          {/* Upcoming / past — M3 segmented button. */}
          <div
            className="inline-flex items-center overflow-hidden rounded-full border border-[hsl(var(--border))]"
            role="group"
            aria-label={dv.meetings.title}
          >
            {(['upcoming', 'past'] as const).map((f, i) => {
              const active = filter === f;
              return (
                <button
                  key={f}
                  type="button"
                  aria-pressed={active}
                  onClick={() => setFilter(f)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium transition-colors ${
                    i > 0 ? 'border-s border-[hsl(var(--border))]' : ''
                  } ${
                    active
                      ? 'bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]'
                      : 'text-foreground hover:bg-black/[0.04] dark:hover:bg-white/[0.06]'
                  }`}
                >
                  {active && <Icon name="check" size={16} />}
                  {dv.meetings[f]}
                </button>
              );
            })}
          </div>
          {terms && terms.length > 0 && (
            <select
              aria-label={dv.meetings.term}
              value={termSel ?? (terms.find((t) => t.isActive)?.id ?? 'all')}
              onChange={(e) => setTermSel(e.target.value)}
              className="select w-auto rounded-full py-1.5"
            >
              {terms.map((t) => (
                <option key={t.id} value={t.id}>{t.number} ވަނަ ދައުރު</option>
              ))}
              <option value="all">{dv.meetings.allTerms}</option>
            </select>
          )}
          {/* Body filter — Council / WDC / all. Passes ?body= to the list. */}
          <select
            aria-label={dv.body.label}
            value={bodyFilter ?? 'all'}
            onChange={(e) => setBodyFilter(e.target.value === 'all' ? null : (e.target.value as Body))}
            className="select w-auto rounded-full py-1.5"
          >
            <option value="all">{dv.body.all}</option>
            {BODIES.map((b) => (
              <option key={b} value={b}>{dv.body[b]}</option>
            ))}
          </select>
        </div>
        <div className="relative w-full sm:w-72">
          <Icon name="search" size={18} className="pointer-events-none absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={dv.meetings.search}
            className="input rounded-full py-1.5 ps-9 pe-4"
          />
        </div>
      </div>

      {isLoading ? (
        <p className="text-sm text-muted-foreground" role="status" aria-live="polite">
          {dv.common.loading}
        </p>
      ) : (
        <section className="rounded-3xl bg-[hsl(var(--chrome))] divide-y divide-black/[0.05] overflow-hidden">
          {(data?.meetings ?? []).map((m) => (
            <Link
              key={m.id}
              to="/meetings/$meetingId"
              params={{ meetingId: m.id }}
              className="flex items-center gap-4 px-4 sm:px-5 py-3.5 transition-colors hover:bg-[hsl(var(--surface-high))]"
            >
              <span className="grid size-11 shrink-0 place-items-center rounded-full bg-[hsl(var(--secondary-container))] text-[hsl(var(--on-secondary-container))]">
                <Icon name="calendar_today" size={22} />
              </span>
              <div className="min-w-0 flex-1">
                <p className="flex flex-wrap items-center gap-x-2 gap-y-1 font-medium leading-snug">
                  <span className="truncate">{m.title}</span>
                  <span className="pill pill-neutral shrink-0">{dv.body[m.body]}</span>
                  {m.meetingType === 'EMERGENCY' && (
                    <span className="pill pill-pending shrink-0">
                      {dv.meetings.typeLabel.EMERGENCY}
                    </span>
                  )}
                </p>
                <div className="mt-1 flex flex-wrap items-center gap-x-3.5 gap-y-1 text-xs text-muted-foreground">
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="calendar_today" size={15} className="shrink-0 text-foreground/40" />
                    {formatDvDate(m.scheduledAt)}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="schedule" size={15} className="shrink-0 text-foreground/40" />
                    <span className="tabular" dir="ltr">{formatDvTime(m.scheduledAt)}</span>
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="location_on" size={15} className="shrink-0 text-foreground/40" />
                    {m.location}
                  </span>
                  <span className="inline-flex items-center gap-1.5">
                    <Icon name="checklist" size={15} className="shrink-0 text-foreground/40" />
                    <span className="tabular" dir="ltr">{m._count.agendaItems}</span> {dv.meetings.itemCount}
                  </span>
                </div>
              </div>
              <span className={`${meetingStatus(m).pill} shrink-0`}>
                {meetingStatus(m).label}
              </span>
            </Link>
          ))}
          {data && data.meetings.length === 0 && (
            <p className="p-8 text-center text-sm text-muted-foreground">{dv.meetings.empty}</p>
          )}
        </section>
      )}

      <Pagination page={page} pageSize={PAGE_SIZE} total={data?.total ?? 0} onChange={setPage} />
    </div>
  );
}
